import { useCallback, useEffect } from 'react'

import { useQuery } from '@apollo/client/react'

import {
  EVIE_PAGE_FACTS_QUERY,
  type EvieFacts,
  type EviePageFactsResult,
} from '../eviePageFactsQuery'

// Initiation has no poke transport because there is no engine case yet;
// event delivery would pull-bootstrap one
// over a mid-intake snapshot. A scoped 10s poll of the THIN read is the
// honest freshness path there (Persona webhook landing after modal close,
// staged step reveals, the initiation→chat flip).
const PRE_ACTIVATION_POLL_MS = 10_000
// Post-activation the poke stream leads; this belt only converges after a
// lost poke or a dead poke bus. Deliberately on its own timer, independent
// of the engine-session lifecycle — a dead engine must not blind the page to
// voi facts (e.g. a screening completion).
const CONVERGENCE_POLL_MS = 45_000

export interface EviePageFactsHandle {
  loading: boolean
  /** null until the first answer lands — the router waits on it. */
  evieEnabled: boolean | null
  facts: EvieFacts | null
  /** The query's error — load-bearing when evieEnabled is still null (the
   * page must render a failure state, not an infinite spinner). */
  error: Error | undefined
  refetch: () => void
}

/**
 * The Evie page's voi-facts handle: one thin query, re-pulled on evie pokes
 * (wired by the surface), a phase-scoped belt, visibility changes, and after
 * own mutations. Replaces useAutoRefresh's blind refetch of the giant
 * legacy query.
 */
export function useEviePageFacts({
  applicantId,
  skip = false,
}: {
  applicantId: number
  skip?: boolean
}): EviePageFactsHandle {
  const { data, loading, error, refetch } = useQuery<{
    eviePageFacts: EviePageFactsResult
  }>(EVIE_PAGE_FACTS_QUERY, {
    variables: { applicantId },
    skip,
  })

  const result = data?.eviePageFacts ?? null
  const facts = result?.facts ?? null

  const safeRefetch = useCallback(() => {
    // Belt/poke refetches are best-effort — the next tick converges.
    void refetch().catch(() => undefined)
  }, [refetch])

  const preActivation = facts != null && facts.currentPhase === 'initiation'
  const beltMs =
    facts == null
      ? null
      : preActivation
        ? PRE_ACTIVATION_POLL_MS
        : CONVERGENCE_POLL_MS

  useEffect(() => {
    if (skip || !result?.evieEnabled || beltMs == null) return
    const interval = setInterval(() => {
      if (document.hidden) return
      safeRefetch()
    }, beltMs)
    return () => clearInterval(interval)
  }, [skip, result?.evieEnabled, beltMs, safeRefetch])

  useEffect(() => {
    if (skip || !result?.evieEnabled) return
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      safeRefetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [skip, result?.evieEnabled, safeRefetch])

  return {
    loading,
    evieEnabled: result?.evieEnabled ?? null,
    facts,
    error,
    refetch: safeRefetch,
  }
}
